
import React, { useState, useEffect } from 'react';
import { Sidebar } from './components/Sidebar';
import { Dashboard } from './components/Dashboard';
import { MarketingDashboard } from './components/MarketingDashboard';
import { CRM } from './components/CRM';
import { Pipeline } from './components/Pipeline';
import { ProposalGenerator } from './components/ProposalGenerator';
import { ContractBuilder } from './components/ContractBuilder';
import { ManagedProperties } from './components/ManagedProperties';
import { Login } from './components/Login';
import { AdminPanel } from './components/AdminPanel';
import { AIConsumptionPanel } from './components/AIConsumptionPanel';
import { MarketingLeadsPanel } from './components/MarketingLeadsPanel';
import { ExpensesPanel } from './components/ExpensesPanel';
import { BillingPanel } from './components/BillingPanel';
import { canAccessBilling } from './services/billingService';
import { FeaturebasePanel } from './components/FeaturebasePanel';
import { FeaturebaseSSO } from './components/FeaturebaseSSO';
import { EstimationPanelWithTabs } from './components/EstimationPanelWithTabs';
import { EstimationIframe } from './components/EstimationIframe';
import { EstimationStatsPanel } from './components/EstimationStatsPanel';
import { SetPassword } from './components/SetPassword';
import { MaintenancePage } from './components/MaintenancePage';
import { MaintenanceBanner } from './components/MaintenanceBanner';
import { ImpersonationBanner } from './components/ImpersonationBanner';
import { MobileWarning } from './components/MobileWarning';
import { Owner, Property, Deal, UserProfile, Agency } from './types';
import { storage } from './services/storage';
import { supabase } from './lib/supabase';
import { Loader, Bell, Terminal } from 'lucide-react';
import { useModal } from './components/ui/ModalProvider';
import { setSentryUser, addSentryBreadcrumb, setSentryTag, setSentryContext } from './lib/sentry';
import { useFeaturebase } from './lib/useFeaturebase';
import { impersonationService } from './services/impersonationService';

function App() {
  const { toast } = useModal();
  const [session, setSession] = useState<any>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // Check if we're on the iframe route
  const isIframeRoute = window.location.pathname === '/iframe/estimation';
  
  // Maintenance State
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState<string>('');
  
  const refreshMaintenanceMode = async () => {
    try {
      const enabled = await storage.isMaintenanceModeEnabled();
      const message = await storage.getMaintenanceMessage();
      setIsMaintenanceMode(enabled);
      setMaintenanceMessage(message);
    } catch (e) {
      console.error('Error checking maintenance mode:', e);
      // En cas d'erreur, on laisse l'app fonctionner
      setIsMaintenanceMode(false);
    }
  };
  
  // Auth State
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [isRecoveryMode, setIsRecoveryMode] = useState(false); // NEW: Recovery State

  // Si on revient d'un OAuth Meta/Google (redirigé depuis l'Edge Function), ouvrir l'onglet correspondant
  // (le traitement du code/state se fait dans AdminPanel)
  useEffect(() => {
    if (!session) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('meta_oauth') === '1') {
      setActiveTab('admin_meta');
    } else if (params.get('gcal_oauth') === '1') {
      setActiveTab('management_settings');
    } else if (params.get('rr_connected') === '1' || params.get('rr_error')) {
      // Retour du callback OAuth RentalReady
      const rrError = params.get('rr_error');
      if (rrError) {
        setNotifications(prev => [...prev, `Échec de la connexion RentalReady (${rrError})`]);
      } else {
        setNotifications(prev => [...prev, 'Agence connectée à RentalReady avec succès']);
      }
      setActiveTab('admin_agencies');
      // Nettoyer l'URL pour éviter de rejouer la notification au refresh
      const cleaned = new URL(window.location.href);
      cleaned.searchParams.delete('rr_connected');
      cleaned.searchParams.delete('rr_error');
      window.history.replaceState({}, '', cleaned.toString());
    }
  }, [session]);
  
  // Navigation State
  const [targetManagedId, setTargetManagedId] = useState<string | null>(null);
  const [targetPipelineDeal, setTargetPipelineDeal] = useState<{ id: string, tab: 'notes' | 'reminders' | 'sequence' } | null>(null);
  const [targetProposalDealId, setTargetProposalDealId] = useState<string | null>(null); // NEW for proposal navigation
  const [targetContractDealId, setTargetContractDealId] = useState<string | null>(null); // NEW for contract navigation
  const [targetMarketingLead, setTargetMarketingLead] = useState<{ id: string, tab?: 'reminders' | 'sequence' } | null>(null); // NEW for marketing lead navigation
  
  // Agency State
  const [availableAgencies, setAvailableAgencies] = useState<Agency[]>([]);
  const [selectedAgencyId, setSelectedAgencyId] = useState<string | null>(null);

  // App Data State
  const [owners, setOwners] = useState<Owner[]>([]);
  const [properties, setProperties] = useState<Property[]>([]);
  const [deals, setDeals] = useState<Deal[]>([]);
  
  // Feature Flags
  const [financeEnabled, setFinanceEnabled] = useState(false);
  const [billingEnabled, setBillingEnabled] = useState(false);
  const [yousignEnabled, setYousignEnabled] = useState(false);
  const [metaEnabled, setMetaEnabled] = useState(false);

  // Notifications
  const [notifications, setNotifications] = useState<string[]>([]);

  // Mobile Detection
  const isMobile = () => {
    if (typeof window === 'undefined') return false;
    const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
    const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;
    const isMobileDevice = mobileRegex.test(userAgent.toLowerCase());
    const isSmallScreen = window.innerWidth <= 768;
    return isMobileDevice || isSmallScreen;
  };

  // Écouter les messages de l'extension Chrome globalement (pour rediriger vers Pipeline si nécessaire)
  useEffect(() => {
    const processedMessages = new Set<string>();
    
    const handleExtensionMessage = (event: MessageEvent) => {
      if (event.data && event.data.type === 'PLACID_OPEN_DEAL_MODAL') {
        // Créer un identifiant unique pour éviter les doublons
        const messageId = `${event.data.url}-${event.data.html?.substring(0, 50)}`;
        
        if (processedMessages.has(messageId)) {
          return; // Déjà traité
        }
        
        processedMessages.add(messageId);
        
        // Nettoyer après 5 secondes
        setTimeout(() => {
          processedMessages.delete(messageId);
        }, 5000);
        
        // Si on n'est pas sur la page Pipeline, y rediriger
        if (activeTab !== 'pipeline') {
          setActiveTab('pipeline');
          // Attendre que Pipeline soit monté puis relayer le message
          setTimeout(() => {
            window.postMessage({
              type: 'PLACID_OPEN_DEAL_MODAL',
              html: event.data.html,
              url: event.data.url,
              data: event.data.data
            }, '*');
          }, 300);
        }
      }
    };

    // Écouter aussi les messages chrome.runtime si disponible
    if (typeof window !== 'undefined' && (window as any).chrome && (window as any).chrome.runtime && (window as any).chrome.runtime.onMessage) {
      (window as any).chrome.runtime.onMessage.addListener((request: any, sender: any, sendResponse: (response: any) => void) => {
        if (request.type === 'PLACID_OPEN_DEAL_MODAL') {
          // Créer un identifiant unique pour éviter les doublons
          const messageId = `${request.url}-${request.html?.substring(0, 50)}`;
          
          if (processedMessages.has(messageId)) {
            sendResponse({ success: false, error: 'Already processed' });
            return true;
          }
          
          processedMessages.add(messageId);
          
          // Nettoyer après 5 secondes
          setTimeout(() => {
            processedMessages.delete(messageId);
          }, 5000);
          
          // Si on n'est pas sur la page Pipeline, y rediriger
          if (activeTab !== 'pipeline') {
            setActiveTab('pipeline');
            // Attendre que Pipeline soit monté puis relayer le message
            setTimeout(() => {
              window.postMessage({
                type: 'PLACID_OPEN_DEAL_MODAL',
                html: request.html,
                url: request.url,
                data: request.data
              }, '*');
            }, 300);
          }
          sendResponse({ success: true });
        }
        return true;
      });
    }

    window.addEventListener('message', handleExtensionMessage);
    return () => window.removeEventListener('message', handleExtensionMessage);
  }, [activeTab]);

  // 0. VÉRIFICATION DU MODE MAINTENANCE
  useEffect(() => {
    // Vérifier immédiatement
    refreshMaintenanceMode();

    // Vérifier périodiquement (toutes les 30 secondes)
    const interval = setInterval(refreshMaintenanceMode, 30000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  // 1. INITIALISATION & AUTH LISTENER
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      checkPasswordRequirement(session);
      if (!session) setIsLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      
      // Ajouter un breadcrumb pour les changements d'authentification
      addSentryBreadcrumb(`Auth state changed: ${event}`, 'auth', 'info', {
        event,
        hasSession: !!session,
        userId: session?.user?.id,
      });
      
      // Mettre à jour le contexte Sentry
      if (session?.user) {
        setSentryTag('auth_event', event);
        setSentryContext('auth_session', {
          userId: session.user.id,
          email: session.user.email,
          event,
        });
      } else {
        setSentryUser(null, null);
      }
      
      // Detect Password Recovery Event
      if (event === 'PASSWORD_RECOVERY') {
          setIsRecoveryMode(true);
      }

      checkPasswordRequirement(session);
      
      if (!session) {
          setOwners([]);
          setProperties([]);
          setDeals([]);
          setUserProfile(null);
          setAvailableAgencies([]);
          setSelectedAgencyId(null);
          setIsLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const checkPasswordRequirement = (session: any) => {
      if (session?.user?.user_metadata?.must_change_password) {
          setMustChangePassword(true);
      } else {
          setMustChangePassword(false);
      }
  };

  // 2. CHARGEMENT DU PROFIL
  useEffect(() => {
      if (session?.user?.id && !mustChangePassword && !isRecoveryMode) {
          if (!userProfile || userProfile.id !== session.user.id) {
              initializeUser(session.user.id);
          }
      }
  }, [session?.user?.id, mustChangePassword, isRecoveryMode]);

  // 3. REPAIR POLLING (Only for profile init)
  useEffect(() => {
      let interval: any;
      
      // Repair polling
      if (session?.user?.id && !userProfile && !mustChangePassword && !isRecoveryMode) {
          interval = setInterval(async () => {
             try {
                 const profile = await storage.getProfile(session.user.id);
                 if (profile) initializeUser(session.user.id);
             } catch (e) {}
          }, 3000);
      }

      return () => {
          if (interval) clearInterval(interval);
      };
  }, [session?.user?.id, userProfile, mustChangePassword, isRecoveryMode]);

  // 4. CHARGEMENT DES DONNÉES
  useEffect(() => {
      if (session && userProfile && !mustChangePassword && !isRecoveryMode) {
          refreshData();
      }
  }, [selectedAgencyId, session?.user?.id, userProfile?.accessLevel, mustChangePassword, isRecoveryMode]); 

  // --- LOGIQUE MÉTIER ---

  // Charger les feature flags
  useEffect(() => {
    const checkFeatureFlags = async () => {
      if (userProfile) {
        const financeEnabledFlag = await storage.isFinanceEnabled();
        setFinanceEnabled(financeEnabledFlag);

        const yousignConfig = await storage.getSystemConfig('yousign_enabled');
        setYousignEnabled(yousignConfig?.enabled === true);

        const metaConfig = await storage.getSystemConfig('meta_enabled');
        setMetaEnabled(metaConfig?.enabled === true);
      }
    };
    checkFeatureFlags();
  }, [userProfile]);

  // Accès Facturation : flag + éventuel mode bêta restreint à certaines agences
  useEffect(() => {
    const checkBillingAccess = async () => {
      if (!userProfile) return;
      const billingConfig = await storage.getBillingConfig();
      setBillingEnabled(canAccessBilling(billingConfig, userProfile, selectedAgencyId, availableAgencies));
    };
    checkBillingAccess();
  }, [userProfile, selectedAgencyId, availableAgencies]);

  // Sécurité : rediriger les prestataires marketing (setters et superviseurs) vers marketing_leads si ils tentent d'accéder à un autre onglet
  useEffect(() => {
    // Ne pas exécuter si userProfile n'est pas encore chargé
    if (!userProfile) return;
    
    const isMarketingProvider = userProfile.role === 'prestataire_marketing';
    if (isMarketingProvider) {
      // Les superviseurs peuvent accéder à marketing_stats
      const allowedTabs = ['marketing_leads', 'estimation', 'dashboard'];
      if (userProfile.isSupervisor) {
        allowedTabs.push('marketing_stats');
      }
      // Ne rediriger que si l'onglet actuel n'est pas autorisé
      if (!allowedTabs.includes(activeTab)) {
        setActiveTab('marketing_leads');
      }
    }
  }, [userProfile, activeTab]);
  

  // Initialiser Featurebase (helpdesk & feedback) - sauf sur la route iframe/estimation
  useFeaturebase(userProfile, session, isIframeRoute);

  const initializeUser = async (userId: string) => {
      if (!userProfile) setIsLoading(true);
      
      try {
          const profile = await storage.getProfile(userId);
          
          if (profile) {
              setUserProfile(profile);
              
              // Si c'est un prestataire marketing (setter ou superviseur), initialiser activeTab à 'dashboard'
              // Ne le faire que si activeTab n'est pas déjà un onglet autorisé
              const isMarketingProvider = profile.role === 'prestataire_marketing';
              if (isMarketingProvider) {
                  const allowedTabs = ['marketing_leads', 'estimation', 'dashboard'];
                  if (profile.isSupervisor) {
                      allowedTabs.push('marketing_stats');
                  }
                  // Ne changer activeTab que s'il n'est pas déjà autorisé
                  if (!allowedTabs.includes(activeTab)) {
                      setActiveTab('dashboard');
                  }
              }
              
              // Mettre à jour le contexte Sentry avec les infos utilisateur
              setSentryUser(profile, session);
              addSentryBreadcrumb('User profile loaded', 'auth', 'info', {
                userId: profile.id,
                role: profile.role,
                accessLevel: profile.accessLevel,
              });
              
              const allAgencies = await storage.getAllAgencies();
              let userAgencies: Agency[] = [];

              if (profile.accessLevel === 'national' || profile.role === 'admin' || profile.role === 'superadmin') {
                  userAgencies = allAgencies;
              } else {
                  const userAgencyIds = await storage.getUserAgencies(userId);
                  userAgencies = allAgencies.filter(a => userAgencyIds.includes(a.id));
              }
              setAvailableAgencies(userAgencies);
              const selectedId = selectedAgencyId || (userAgencies.length > 0 && profile.accessLevel === 'local' ? userAgencies[0].id : null);
              if (userAgencies.length > 0 && profile.accessLevel === 'local') {
                  setSelectedAgencyId(prev => prev || userAgencies[0].id);
              }
              
              // Ajouter le contexte des agences dans Sentry
              setSentryContext('user_agencies', {
                availableCount: userAgencies.length,
                selectedAgencyId: selectedId,
                agencyIds: userAgencies.map(a => a.id),
              });
          }
      } catch (e) {
          console.error("Erreur init:", e);
          addSentryBreadcrumb('Error initializing user', 'error', 'error', {
            userId,
            error: String(e),
          });
      } finally {
          setIsLoading(false);
      }
  };

  const refreshData = async (background: boolean = false) => {
      if (userProfile?.accessLevel === 'local' && !selectedAgencyId) return;

      if (!background) setIsDataLoading(true);
      
      addSentryBreadcrumb('Refreshing data', 'data', 'info', {
        agencyId: selectedAgencyId,
        background,
      });
      
      try {
          const [fetchedOwners, fetchedProps, fetchedDeals] = await Promise.all([
              storage.getOwners(selectedAgencyId),
              storage.getProperties(selectedAgencyId),
              storage.getDeals(selectedAgencyId)
          ]);
          setOwners(fetchedOwners || []);
          setProperties(fetchedProps || []);
          setDeals(fetchedDeals || []);
          
          // Ajouter le contexte des données chargées
          setSentryContext('app_data', {
            ownersCount: fetchedOwners?.length || 0,
            propertiesCount: fetchedProps?.length || 0,
            dealsCount: fetchedDeals?.length || 0,
            agencyId: selectedAgencyId,
          });
          
          addSentryBreadcrumb('Data refreshed successfully', 'data', 'info', {
            owners: fetchedOwners?.length || 0,
            properties: fetchedProps?.length || 0,
            deals: fetchedDeals?.length || 0,
          });
      } catch (e) {
          console.error("Erreur chargement:", e);
          addSentryBreadcrumb('Error refreshing data', 'error', 'error', {
            error: String(e),
            agencyId: selectedAgencyId,
          });
      } finally {
          if (!background) setIsDataLoading(false);
      }
  };

  const handleAddOwner = async (owner: Owner) => {
      // Prioritize selectedAgencyId, fallback to owner.agencyId (from modal), fallback to null
      const agencyIdToSave = selectedAgencyId || owner.agencyId;
      const ownerWithAgency = { ...owner, agencyId: agencyIdToSave };
      await storage.saveOwner(ownerWithAgency);
      refreshData();
      toast.success("Propriétaire ajouté avec succès.");
  };
  
  const handleSaveOwner = async (owner: Owner) => {
      const agencyIdToSave = selectedAgencyId || owner.agencyId;
      const ownerWithAgency = { ...owner, agencyId: agencyIdToSave };
      await storage.saveOwner(ownerWithAgency);
      refreshData(true); // Background refresh
      // Pas de toast pour éviter le spam lors des modifications fréquentes
  };
  
  const handleSaveProperty = async (property: Property) => {
      // 1. Trouver le propriétaire du bien pour récupérer son agence
      const owner = owners.find(o => o.id === property.ownerId);
      
      // 2. Priorité : Agence du propriétaire > Agence sélectionnée (vue locale) > Agence existante du bien
      const agencyIdToSave = owner?.agencyId || selectedAgencyId || property.agencyId;
      
      const propWithAgency = { ...property, agencyId: agencyIdToSave };
      
      const { error } = await storage.saveProperty(propWithAgency);
      if (error) {
          toast.error("Erreur lors de la sauvegarde du bien.");
      } else {
          // toast.success("Bien enregistré avec succès."); // Supprimé pour éviter le spam lors de l'autosave
          refreshData(true); // CRITICAL: Background refresh to avoid unmounting ManagedProperties
      }
  };
  
  const handleAddDealUnified = async (deal: Deal, owner?: Owner, property?: Property) => {
      // Priorité à l'agence sélectionnée dans le Deal (venant de la modale), sinon l'agence globale sélectionnée
      const finalAgencyId = deal.agencyId || selectedAgencyId;
      const dealWithAgency = { ...deal, agencyId: finalAgencyId };

      if (owner && property) {
          // Vérifier si le propriétaire existe déjà (a un ID)
          if (owner.id) {
              // Propriétaire existant : créer seulement le bien et le deal
              const propertyWithAgency = { ...property, agencyId: finalAgencyId };
              const { data: propData, error: propError } = await storage.saveProperty(propertyWithAgency);
              
              if (propError || !propData) {
                  toast.error("Erreur lors de la création du bien.");
                  return;
              }
              
              const dealWithProperty = {
                  ...dealWithAgency,
                  ownerId: owner.id,
                  propertyId: propData.id!
              };
              
              await storage.saveDeal(dealWithProperty);
          } else {
              // Nouveau propriétaire : création complète (owner + property + deal)
              const ownerWithAgency = { ...owner, agencyId: finalAgencyId };
              const propertyWithAgency = { ...property, agencyId: finalAgencyId };
              
              await storage.createFullDeal(dealWithAgency, ownerWithAgency, propertyWithAgency, finalAgencyId);
          }
      } else {
          // Standard creation (deal seul)
          await storage.saveDeal(dealWithAgency);
      }
      
      // Attendre un court délai pour s'assurer que la base de données est à jour
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Attendre le rafraîchissement des données avant d'afficher le message de succès
      await refreshData();
      toast.success("Affaire créée avec succès !");
  };

  const handleUpdateDeal = async (deal: Deal) => {
      // Optimistic Update
      setDeals(prev => prev.map(d => d.id === deal.id ? deal : d));
      
      await storage.saveDeal(deal);
      refreshData(true); // Background refresh
  };

  const handleNavigateToManaged = (propertyId: string) => {
      setTargetManagedId(propertyId);
      setActiveTab('managed');
  };

  const handleNavigateToPipelineDeal = (dealId: string, tab: 'notes' | 'reminders' | 'sequence') => {
      setTargetPipelineDeal({ id: dealId, tab });
      setActiveTab('pipeline');
  };

  const handleNavigateToProposal = (dealId: string) => {
      setTargetProposalDealId(dealId);
      setActiveTab('proposal');
  };

  const handleNavigateToContract = (dealId: string) => {
      setTargetContractDealId(dealId);
      setActiveTab('contract');
  };

  const handleNavigateToMarketingLead = (leadId: string, tab?: 'reminders' | 'sequence') => {
      setTargetMarketingLead({ id: leadId, tab });
      setActiveTab('marketing_leads');
  };

  // Fonction pour gérer le changement d'onglet depuis la sidebar
  const handleTabChange = (tab: string) => {
      // Si c'est un prestataire marketing, vérifier les onglets autorisés
      const isMarketingProvider = userProfile?.role === 'prestataire_marketing';
      if (isMarketingProvider) {
          const allowedTabs = ['marketing_leads', 'estimation', 'dashboard'];
          if (userProfile?.isSupervisor) {
              allowedTabs.push('marketing_stats');
          }
          if (!allowedTabs.includes(tab)) {
              // Rediriger automatiquement vers marketing_leads si tentative d'accès à un autre onglet
              setActiveTab('marketing_leads');
              addSentryBreadcrumb(`Tab change blocked for marketing provider, redirected to marketing_leads`, 'navigation', 'warning', { attemptedTab: tab });
              setSentryTag('active_tab', 'marketing_leads');
              return;
          }
      }
      
      setActiveTab(tab);
      // Ajouter un breadcrumb pour la navigation
      addSentryBreadcrumb(`Tab changed to: ${tab}`, 'navigation', 'info', { tab });
      setSentryTag('active_tab', tab);
      // Réinitialiser les états de navigation pour forcer le mode liste
      if (tab === 'proposal') {
          setTargetProposalDealId(null);
      } else if (tab === 'contract') {
          setTargetContractDealId(null);
      }
  };

  // --- DELETE HANDLERS ---
  const handleDeleteOwner = async (ownerId: string) => {
      const { error } = await storage.deleteOwner(ownerId);
      if (error) {
          toast.error("Erreur lors de la suppression du propriétaire.");
      } else {
          refreshData();
          toast.success("Propriétaire supprimé.");
      }
  };

  const handleDeleteProperty = async (propertyId: string) => {
      const { error } = await storage.deleteProperty(propertyId);
      if (error) {
          toast.error("Erreur lors de la suppression du bien.");
      } else {
          refreshData();
          toast.success("Bien supprimé.");
      }
  };

  const handleDeleteDeal = async (dealId: string) => {
      const { error } = await storage.deleteDeal(dealId);
      if (error) {
          toast.error("Erreur lors de la suppression de l'affaire.");
      } else {
          refreshData();
          toast.success("Affaire supprimée.");
      }
  };

  const handleLogout = async () => {
      await storage.logout();
      setSession(null);
      setUserProfile(null);
  };

  const handlePasswordSuccess = () => {
      setMustChangePassword(false);
      setIsRecoveryMode(false);
      // Remove URL fragment to prevent re-triggering recovery mode on refresh
      window.history.replaceState(null, '', window.location.pathname);
      window.location.reload(); 
  };

  // --- ROUTE IFRAME (priorité absolue, pas d'authentification requise) ---
  if (isIframeRoute) {
    return <EstimationIframe />;
  }

  // --- ROUTE SSO FEATUREBASE (priorité absolue, authentification gérée dans le composant) ---
  if (window.location.pathname === '/sso/featurebase') {
    return <FeaturebaseSSO />;
  }

  // --- DÉTECTION MOBILE (priorité absolue) ---
  if (isMobile()) {
    return <MobileWarning />;
  }

  if (isLoading && !userProfile && !session) return <div className="h-screen flex items-center justify-center bg-gray-50"><Loader className="w-8 h-8 animate-spin text-amber-500" /></div>;

  // Vérifier s'il y a un paramètre pour bypasser la maintenance
  const urlParams = new URLSearchParams(window.location.search);
  const forceParam = urlParams.get('force');
  const bypassMaintenance = forceParam === 'maintenance_login';

  if (!session) {
      // Vérifier aussi la maintenance pour les utilisateurs non connectés (sauf si bypass)
      if (isMaintenanceMode && !bypassMaintenance) {
          return <MaintenancePage message={maintenanceMessage} />;
      }
      return <Login onLogin={() => {}} />;
  }

  // --- MODE MAINTENANCE (sauf pour superadmin et si bypass activé) ---
  if (isMaintenanceMode && userProfile?.role !== 'superadmin' && !bypassMaintenance) {
      return <MaintenancePage message={maintenanceMessage} />;
  }

  // --- ECRAN SET PASSWORD (FORCE OU RECOVERY) ---
  if (mustChangePassword || isRecoveryMode) {
      return <SetPassword onSuccess={handlePasswordSuccess} />;
  }

  // --- ECRAN SETUP (DB manquant ou profil non chargé) ---
  if (!userProfile) {
      return (
          <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
              <div className="max-w-xl w-full bg-white rounded-2xl shadow-2xl p-8 border-t-4 border-amber-500 text-center">
                  <div className="bg-amber-100 p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-6">
                      <Terminal className="w-8 h-8 text-amber-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900 mb-2">Configuration requise</h2>
                  <p className="text-slate-500 mb-8">
                      Votre profil utilisateur n'est pas encore configuré ou la base de données est en cours d'initialisation.
                      Veuillez contacter votre administrateur si le problème persiste.
                  </p>
                  
                  <button onClick={() => window.location.reload()} className="bg-slate-900 text-white px-6 py-3 rounded-lg font-bold hover:bg-slate-800 transition-colors w-full">
                      Rafraîchir la page
                  </button>
              </div>
          </div>
      );
  }

  const renderContent = () => {
    if (isDataLoading) {
        return <div className="h-full flex items-center justify-center"><Loader className="w-8 h-8 animate-spin text-slate-300" /></div>
    }

    switch (activeTab) {
      case 'dashboard':
        // Les prestataires marketing voient le MarketingDashboard
        if (userProfile?.role === 'prestataire_marketing') {
          return <MarketingDashboard 
            userProfile={userProfile}
            onNavigateToLead={handleNavigateToMarketingLead}
          />;
        }
        // Les autres utilisateurs voient le Dashboard classique
        return <Dashboard 
            deals={deals} 
            owners={owners} 
            properties={properties}
            agencies={availableAgencies}
            userProfile={userProfile}
            onUpdateDeal={handleUpdateDeal}
            onDeleteDeal={handleDeleteDeal}
            onNavigateToDeal={handleNavigateToPipelineDeal}
        />;
      case 'crm':
        return <CRM 
            owners={owners} 
            properties={properties} 
            deals={deals}
            onAddOwner={handleAddOwner}
            onSaveProperty={handleSaveProperty}
            onDeleteOwner={handleDeleteOwner}
            onDeleteProperty={handleDeleteProperty}
            userRole={userProfile.role} 
            agencies={availableAgencies} 
            onNavigateToManaged={handleNavigateToManaged}
            onAddDeal={handleAddDealUnified}
            userProfile={userProfile}
            selectedAgencyId={selectedAgencyId}
            onRefreshData={() => refreshData()}
            onNavigateToDeal={handleNavigateToPipelineDeal}
        />;
      case 'pipeline':
        return <Pipeline 
            deals={deals} 
            owners={owners} 
            properties={properties} 
            agencies={availableAgencies}
            userProfile={userProfile}
            setDeals={setDeals} 
            onUpdateDeal={handleUpdateDeal}
            onAddDeal={handleAddDealUnified}
            onUpdateOwner={handleSaveOwner}
            onUpdateProperty={handleSaveProperty}
            onDeleteDeal={handleDeleteDeal}
            initialDealId={targetPipelineDeal?.id}
            initialTab={targetPipelineDeal?.tab}
            onClearInitialDeal={() => setTargetPipelineDeal(null)}
            onNavigateToProposal={handleNavigateToProposal}
            onNavigateToContract={handleNavigateToContract} // NEW
            onNavigateToManaged={handleNavigateToManaged}
        />;
      case 'managed':
        return <ManagedProperties 
            properties={properties}
            owners={owners}
            deals={deals} // Added prop
            onSaveProperty={handleSaveProperty}
            initialPropertyId={targetManagedId}
            financeEnabled={financeEnabled}
            onClearInitialId={() => setTargetManagedId(null)}
            onNavigateToProposal={handleNavigateToProposal} // Added prop
            onRefreshData={() => refreshData(true)} // Recharger les données après suppression
        />;
      case 'proposal':
        return <ProposalGenerator 
            dealId={targetProposalDealId}
            selectedAgencyId={selectedAgencyId} 
            onBack={() => { 
                refreshData(true); // Trigger data refresh to update Deal with new proposal info
                setTargetProposalDealId(null); 
                setActiveTab('pipeline'); 
            }} 
            onNavigateToDeal={(id) => {
                refreshData(true); // Trigger data refresh
                handleNavigateToPipelineDeal(id, 'notes');
            }}
            onNavigateToContract={handleNavigateToContract} // NEW
            onNavigateToManaged={(id) => {
                refreshData(true);
                handleNavigateToManaged(id);
            }}
            onDealUpdated={() => refreshData(true)} // Rafraîchir les données après chaque mise à jour de deal
        />;
      case 'contract':
        return <ContractBuilder 
            dealId={targetContractDealId || targetPipelineDeal?.id} 
            selectedAgencyId={selectedAgencyId}
            onBack={() => { 
                setTargetContractDealId(null);
                setActiveTab('pipeline');
            }}
            onDealUpdated={() => refreshData(true)} // Rafraîchir les données après chaque mise à jour de deal
            onNavigateToManaged={handleNavigateToManaged}
            userProfile={userProfile} // PASS PROFILE HERE
            yousignEnabled={yousignEnabled}
        />;
      case 'admin_users':
        return (userProfile?.role === 'admin' || userProfile?.role === 'superadmin') ? <AdminPanel view="users" userProfile={userProfile} /> : <div className="p-8">Accès refusé</div>;
      case 'admin_agencies':
        return (userProfile?.role === 'admin' || userProfile?.role === 'superadmin') ? <AdminPanel view="agencies" userProfile={userProfile} /> : <div className="p-8">Accès refusé</div>;
      case 'admin_sequences':
        return (userProfile?.role === 'admin' || userProfile?.role === 'superadmin') ? <AdminPanel view="sequences" userProfile={userProfile} /> : <div className="p-8">Accès refusé</div>;
      case 'admin_documents':
        return (userProfile?.role === 'admin' || userProfile?.role === 'superadmin') ? <AdminPanel view="documents" userProfile={userProfile} /> : <div className="p-8">Accès refusé</div>;
      case 'admin_checklists':
        return (userProfile?.role === 'admin' || userProfile?.role === 'superadmin') ? <AdminPanel view="checklists" userProfile={userProfile} /> : <div className="p-8">Accès refusé</div>;
      case 'admin_ai_consumption':
        return userProfile?.role === 'superadmin' ? <AIConsumptionPanel userProfile={userProfile} /> : <div className="p-8">Accès refusé</div>;
      case 'admin_meta':
        if (!metaEnabled) {
          return (
            <div className="p-8">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                <h2 className="text-lg font-bold text-yellow-900 mb-2">Fonctionnalité désactivée</h2>
                <p className="text-yellow-800">
                  La gestion Meta est désactivée. Veuillez l'activer dans les paramètres pour accéder à cette fonctionnalité.
                </p>
              </div>
            </div>
          );
        }
        return (userProfile?.role === 'admin' || userProfile?.role === 'superadmin') ? <AdminPanel view="meta" userProfile={userProfile} /> : <div className="p-8">Accès refusé</div>;
      case 'management_settings':
        return userProfile?.role !== 'prestataire_marketing'
          ? <AdminPanel view="management-settings" userProfile={userProfile} selectedAgencyId={selectedAgencyId} />
          : <div className="p-8">Accès refusé</div>;
      case 'marketing_stats':
        if (!metaEnabled) {
          return (
            <div className="p-8">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                <h2 className="text-lg font-bold text-yellow-900 mb-2">Fonctionnalité désactivée</h2>
                <p className="text-yellow-800">
                  La gestion des leads prestataires marketing est désactivée. Veuillez l'activer dans les paramètres pour accéder à cette fonctionnalité.
                </p>
              </div>
            </div>
          );
        }
        // Accessible aux admins, superadmins et superviseurs prestataires marketing
        const canAccessStats = userProfile?.role === 'admin' || 
                              userProfile?.role === 'superadmin' || 
                              (userProfile?.role === 'prestataire_marketing' && userProfile?.isSupervisor);
        return canAccessStats ? <AdminPanel view="stats" userProfile={userProfile} /> : <div className="p-8">Accès refusé</div>;
      case 'admin_audit_logs':
        return userProfile?.role === 'superadmin' ? <AdminPanel view="audit-logs" userProfile={userProfile} /> : <div className="p-8">Accès refusé</div>;
      case 'admin_settings':
        return userProfile?.role === 'superadmin' ? <AdminPanel view="settings" userProfile={userProfile} /> : <div className="p-8">Accès refusé</div>;
      case 'admin_marketing':
        return (userProfile?.role === 'admin' || userProfile?.role === 'superadmin') ? <AdminPanel view="marketing" userProfile={userProfile} /> : <div className="p-8">Accès refusé</div>;
      case 'marketing_leads':
        if (!metaEnabled) {
          return (
            <div className="p-8">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                <h2 className="text-lg font-bold text-yellow-900 mb-2">Fonctionnalité désactivée</h2>
                <p className="text-yellow-800">
                  La gestion Meta est désactivée. Veuillez l'activer dans les paramètres pour accéder à cette fonctionnalité.
                </p>
              </div>
            </div>
          );
        }
        return (userProfile?.role === 'prestataire_marketing' || userProfile?.role === 'admin' || userProfile?.role === 'superadmin') ? (
          <MarketingLeadsPanel 
            userProfile={userProfile}
            initialLeadId={targetMarketingLead?.id}
            initialTab={targetMarketingLead?.tab}
            onClearInitialLead={() => setTargetMarketingLead(null)}
          />
        ) : <div className="p-8">Accès refusé</div>;
      case 'expenses':
        if (!financeEnabled) {
          return (
            <div className="p-8">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                <h2 className="text-lg font-bold text-yellow-900 mb-2">Fonctionnalité désactivée</h2>
                <p className="text-yellow-800">
                  La gestion des finances est actuellement désactivée. Veuillez contacter un administrateur pour activer cette fonctionnalité.
                </p>
              </div>
            </div>
          );
        }
        return <ExpensesPanel userProfile={userProfile} selectedAgencyId={selectedAgencyId} />;
      case 'billing':
        if (!billingEnabled) {
          return (
            <div className="p-8">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                <h2 className="text-lg font-bold text-yellow-900 mb-2">Fonctionnalité désactivée</h2>
                <p className="text-yellow-800">
                  La facturation propriétaires n'est pas activée pour votre agence (fonctionnalité désactivée ou en bêta restreinte). Veuillez contacter un administrateur.
                </p>
              </div>
            </div>
          );
        }
        return <BillingPanel userProfile={userProfile} selectedAgencyId={selectedAgencyId} />;
      case 'featurebase':
        return <FeaturebasePanel 
            userProfile={userProfile}
            session={session}
        />;
      case 'estimation':
        return <EstimationPanelWithTabs />;
      case 'estimation_stats':
        return (userProfile?.role === 'admin' || userProfile?.role === 'superadmin') 
          ? <EstimationStatsPanel /> 
          : <div className="p-8">Accès refusé</div>;
      default:
        return <Dashboard deals={deals} owners={owners} />;
    }
  };

  return (
    <div className="flex bg-gray-50 h-screen font-sans overflow-hidden">
      <Sidebar 
        activeTab={activeTab} 
        setActiveTab={handleTabChange} 
        userProfile={userProfile}
        availableAgencies={availableAgencies}
        selectedAgencyId={selectedAgencyId}
        setSelectedAgencyId={setSelectedAgencyId}
        onLogout={handleLogout}
        metaEnabled={metaEnabled}
      />
      
      <div className="flex-1 ml-64 relative h-full flex flex-col overflow-hidden">
        {/* Bandeau d'impersonation */}
        {impersonationService.isImpersonating() && (
          <ImpersonationBanner 
            onImpersonationStopped={() => {
              // Recharger les données après l'arrêt de l'impersonation
              window.location.reload();
            }} 
          />
        )}
        {/* Bandeau de maintenance pour superadmin */}
        {isMaintenanceMode && userProfile?.role === 'superadmin' && (
          <MaintenanceBanner onMaintenanceDisabled={refreshMaintenanceMode} />
        )}
        {/* Notifications Toast */}
        <div className="absolute top-4 right-4 z-50 space-y-2">
            {notifications.map((notif, i) => (
                <div key={i} className="bg-white border-l-4 border-amber-500 p-4 shadow-lg rounded flex items-center animate-in slide-in-from-right">
                    <Bell className="w-5 h-5 text-amber-500 mr-3" />
                    <span className="text-sm font-medium text-slate-800">{notif}</span>
                </div>
            ))}
        </div>

        {renderContent()}
      </div>
    </div>
  );
}

export default App;
